import { Box, BoxProps, IconButton, IconButtonProps } from '@chakra-ui/react'
import {
  Autoplay,
  Grid,
  Navigation,
  Pagination,
  Swiper,
} from '@components/ui/carousel/slider'
import { forwardRef, useRef } from 'react'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import { IconBaseProps } from 'react-icons/lib'
import 'swiper/css/autoplay'
import 'swiper/css/grid'
import 'swiper/css/pagination'
import { SwiperProps, SwiperRef } from 'swiper/react'

interface CarouselIconButtonProps extends IconButtonProps {
  iconProps?: IconBaseProps
}

interface CarouselPropsType extends SwiperProps {
  children: React.ReactNode
  className?: string
  prevButtonStyleProps?: CarouselIconButtonProps
  nextButtonStyleProps?: CarouselIconButtonProps
  prevActivateId?: string
  nextActivateId?: string
  buttonSize?: 'default' | 'small'
  centeredSlides?: boolean
  loop?: boolean
  slidesPerView?: number
  parentBoxStyleProps?: BoxProps
}

const Carousel = forwardRef<SwiperRef, CarouselPropsType>(
  (
    {
      children,
      className = '',
      prevActivateId = '',
      nextActivateId = '',
      prevButtonStyleProps: {
        iconProps: prevButtonIconProps,
        ...restPrevButtonStyleProps
      } = {},
      nextButtonStyleProps: {
        iconProps: nextButtonIconProps,
        ...restNextButtonStyleProps
      } = {},
      buttonSize = 'default',
      breakpoints,
      navigation = true,
      pagination = false,
      loop = false,
      grid,
      autoplay,
      parentBoxStyleProps,
      ...props
    },
    ref,
  ) => {
    const prevRef = useRef<HTMLButtonElement>(null)
    const nextRef = useRef<HTMLButtonElement>(null)

    return (
      <Box
        className={`carouselWrapper relative ${className} ${
          pagination ? 'dotsCircle' : 'dotsCircleNone'
        }`}
        role="group"
        {...parentBoxStyleProps}
      >
        <Swiper
          modules={[Navigation, Autoplay, Pagination, Grid]}
          autoplay={autoplay}
          breakpoints={breakpoints}
          pagination={pagination}
          ref={ref}
          grid={grid}
          navigation={
            navigation
              ? {
                  prevEl: prevActivateId.length
                    ? `#${prevActivateId}`
                    : prevRef.current,
                  nextEl: nextActivateId.length
                    ? `#${nextActivateId}`
                    : nextRef.current,
                }
              : {}
          }
          {...props}
        >
          {children}
        </Swiper>
        {Boolean(navigation) && (
          <>
            <IconButton
              aria-label="Previous"
              id={prevActivateId.length > 0 ? prevActivateId : undefined}
              ref={prevActivateId.length > 0 ? undefined : prevRef}
              icon={<IoIosArrowBack {...prevButtonIconProps} />}
              rounded="full"
              position="absolute"
              top="50%"
              transform="translateY(-50%)"
              zIndex="dropdown"
              colorScheme="primary"
              {...restPrevButtonStyleProps}
            />

            <IconButton
              aria-label="Next"
              icon={<IoIosArrowForward {...nextButtonIconProps} />}
              id={nextActivateId.length > 0 ? nextActivateId : undefined}
              ref={nextActivateId.length > 0 ? undefined : nextRef}
              rounded="full"
              position="absolute"
              top="50%"
              right="0"
              transform="translateY(-50%)"
              zIndex="dropdown"
              colorScheme="primary"
              {...restNextButtonStyleProps}
            />
          </>
        )}
      </Box>
    )
  },
)

Carousel.displayName = 'Carousel'

export default Carousel
